/**
 * Adapter that bridges the local ApiBid state to the BidCalculationEngine.
 *
 * Converts the flat ApiBid shape into the structured GroupInputNode tree
 * expected by the engine, runs computations, and writes derived values
 * back into the bid object in-place.
 *
 * This avoids duplicating any formulas — all math lives in BidCalculationEngine.
 */

import type { ApiBid, ApiLineGroup } from "../mappers/types";
import {
    computeLineItemDerived,
    computeGroupDerived,
    type LineItemDerived,
} from "../../engine/BidCalculationEngine";

// ── Adapter: ApiBid → BidCalculationEngine input format ────────────────────

interface GroupInputNode {
    id: number;
    children: GroupInputNode[];
    lineItems: any[];
    addons: any[];
}

/**
 * Converts a flat list of groups and items into a tree of GroupInputNodes.
 */
function buildGroupTree(
    allGroups: ApiLineGroup[],
    allItems: any[],
    allAddons: any[],
): GroupInputNode[] {
    const nodeMap = new Map<number, GroupInputNode>();

    // Create nodes
    allGroups.forEach((g) => {
        nodeMap.set(g.id, {
            id: g.id,
            children: [],
            lineItems: [],
            addons: [],
        });
    });

    // Assign line items to their groups
    allItems.forEach((item) => {
        const node = nodeMap.get(item.line_group_id);
        if (node) {
            node.lineItems.push({
                vendor_rate: Number(item.vendor_rate) || 0,
                markup: Number(item.markup) || 0,
                markup_amount: Number(item.markup_amount) || 0,
                budget_buffer: Number(item.budget_buffer) || 0,
                budget_buffer_amount: Number(item.budget_buffer_amount) || 0,
                quantity: Number(item.quantity) || 0,
                days: Number(item.days) || 0,
                adjustment: Number(item.adjustment) || 0,
                ot_1_5: Number(item.ot_1_5) || 0,
                ot_2_0: Number(item.ot_2_0) || 0,
                vendor_client_exchange_rate_buffered:
                    Number(item.vendor_client_exchange_rate_buffered) || 1,
                is_internal: !!item.is_internal,
                internal_rate:
                    item.internal_rate != null
                        ? Number(item.internal_rate)
                        : null,
                internal_markup:
                    item.internal_markup != null
                        ? Number(item.internal_markup)
                        : null,
            });
        }
    });

    // Assign addons to their groups
    allAddons.forEach((addon) => {
        if (addon.line_group_id) {
            const node = nodeMap.get(addon.line_group_id);
            if (node) {
                node.addons.push({
                    id: addon.id,
                    amount: Number(addon.amount) || 0,
                    minimum_amount:
                        addon.minimum_amount != null
                            ? Number(addon.minimum_amount)
                            : null,
                    type: addon.type || "amount",
                    line_group_id: addon.line_group_id,
                });
            }
        }
    });

    // Build parent-child hierarchy
    allGroups.forEach((g) => {
        if (g.parent_id) {
            const parent = nodeMap.get(g.parent_id);
            const child = nodeMap.get(g.id);
            if (parent && child) parent.children.push(child);
        }
    });

    // Return root nodes (no parent)
    return allGroups
        .filter((g) => !g.parent_id)
        .map((g) => nodeMap.get(g.id)!)
        .filter(Boolean);
}

// ── Write back: engine results → ApiBid ────────────────────────────────────

/**
 * Writes computed line item derived values back into the flat item objects.
 */
function writeBackLineItems(
    allItems: any[],
    allGroups: ApiLineGroup[],
    rootNodes: ReturnType<typeof computeGroupDerived>[],
): void {
    // Build a flat map of line item results indexed by group + position
    const resultMap = new Map<string, LineItemDerived>();

    function walkGroup(computed: ReturnType<typeof computeGroupDerived>): void {
        const groupItems = allItems.filter(
            (i) => i.line_group_id === computed.id,
        );
        computed.lineItemsDerived.forEach((derived, idx) => {
            if (groupItems[idx]) {
                const key = `${computed.id}-${idx}`;
                resultMap.set(key, derived);
            }
        });
        computed.children.forEach(walkGroup);
    }
    rootNodes.forEach(walkGroup);

    // Write derived values back
    const groupItemCounts = new Map<number, number>();
    allItems.forEach((item) => {
        const count = groupItemCounts.get(item.line_group_id) || 0;
        const key = `${item.line_group_id}-${count}`;
        const derived = resultMap.get(key);
        if (derived) {
            item.client_rate = derived.client_rate;
            item.client_rate_budget_buffered =
                derived.client_rate_budget_buffered;
            item.client_rate_quoted = derived.client_rate_quoted;
            item.client_budget_buffered = derived.client_budget_buffered;
            item.client_quoted = derived.client_quoted;
        }
        groupItemCounts.set(item.line_group_id, count + 1);
    });
}

/**
 * Writes computed group derived values back into the flat group objects.
 */
function writeBackGroups(
    allGroups: ApiLineGroup[],
    rootNodes: ReturnType<typeof computeGroupDerived>[],
): void {
    const resultMap = new Map<
        number,
        ReturnType<typeof computeGroupDerived>["derived"]
    >();

    function walkGroup(computed: ReturnType<typeof computeGroupDerived>): void {
        resultMap.set(computed.id, computed.derived);
        computed.children.forEach(walkGroup);
    }
    rootNodes.forEach(walkGroup);

    allGroups.forEach((group) => {
        const derived = resultMap.get(group.id);
        if (derived) {
            group.client_budget = derived.client_budget_buffered;
            group.client_quoted = derived.client_quoted;
        }
    });
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Recalculates all derived fields on a local bid object (in-place mutation).
 *
 * Delegates all math to BidCalculationEngine — this function is purely
 * an adapter between the flat ApiBid shape and the engine's tree input.
 *
 * Call after any field edit to keep totals in sync without a backend round-trip.
 */
export function recalculateLocalBid(bid: ApiBid): ApiBid {
    // Deep clone to avoid "Cannot assign to read only property" on frozen
    // objects (e.g. axios response data passed as initialBid).
    const clone = structuredClone(bid);
    const allGroups = clone.line_groups || [];
    const allItems = (clone as any).line_items || [];
    const allAddons = (clone as any).addons || [];

    if (allGroups.length === 0) return clone;

    // Build tree and compute
    const rootNodes = buildGroupTree(allGroups, allItems, allAddons);
    const computedRoots = rootNodes.map(computeGroupDerived);

    // Write results back into the flat bid data
    writeBackLineItems(allItems, allGroups, computedRoots);
    writeBackGroups(allGroups, computedRoots);

    return clone;
}
